import axios from 'axios';

// Determine API base URL based on environment
export const getApiBaseUrl = () => {
  // If VITE_API_BASE_URL is set, use it (highest priority)
  if (import.meta.env.VITE_FAST_API_URL) {
    return import.meta.env.VITE_FAST_API_URL;
  }


  // For production, use relative URL (inherits protocol from current page)
  if (import.meta.env.PROD) {
    // This will use the same host and protocol as the frontend
    // Assuming the API is proxied through the same domain
    return '';
  }

  // For development, default to localhost:8081
  return 'http://localhost:8081';
};

const API_BASE_URL = getApiBaseUrl();

// Create axios instance for general API calls
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for logging in development
if (import.meta.env.DEV) {
  api.interceptors.request.use(
    (config) => {
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, config.data);
      return config;
    },
    (error) => {
      console.error('[API Request Error]', error);
      return Promise.reject(error);
    }
  );

  api.interceptors.response.use(
    (response) => {
      console.log(`[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`, response.data);
      return response;
    },
    (error) => {
      console.error('[API Response Error]', error.response?.data || error.message);
      return Promise.reject(error);
    }
  );
}

// ============================================================================
// WBA (WhatsApp Business Account) API Methods
// ============================================================================

export interface EmbeddedSignupInitRequest {
  twilio_number_id: string;
  redirect_url?: string;
}

export interface EmbeddedSignupResponse {
  signup_url: string;
  state: string;
  config_id: string;
}

export interface EmbeddedSignupCallbackRequest {
  code: string;
  state: string;
}

export interface WABASetupResponse {
  wba_id: string;
  phone_number_id: string;
  business_id: string;
  display_phone_number: string;
  verified_name: string;
  wba_uuid: string;
  access_token_expires_in?: number;
}

export const wbaAPI = {
  /**
   * Initiate Embedded Sign Up flow for WhatsApp Business
   */
  initiateEmbeddedSignup: (data: EmbeddedSignupInitRequest, token: string) =>
    api.post<EmbeddedSignupResponse>('/api/v1/wba/embedded-signup/initiate', data, {
      headers: { Authorization: `Bearer ${token}` },
    }),

  /**
   * Handle OAuth callback from Meta
   */
  handleCallback: (data: EmbeddedSignupCallbackRequest) =>
    api.post<WABASetupResponse>('/api/v1/wba/embedded-signup/callback', data),

  /**
   * Check if tenant has a WBA assigned
   */
  checkAssignment: (tenantUuid: string, token: string) =>
    api.get(`/api/v1/wba/check-assignment/${tenantUuid}`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
};

export default api;
